import { ReactNode, useEffect, useState, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import { ArrowUp } from 'lucide-react';
import { useLocation } from 'wouter';

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [location] = useLocation();
  const isHomePage = location === '/';
  // Add smooth scrolling behavior to all anchor links
  useEffect(() => {
    const handleSmoothScroll = (e: Event) => {
      e.preventDefault();
      const element = e.currentTarget as HTMLAnchorElement;
      const href = element.getAttribute('href') || '';

      if (href === '#') {
        return;
      }

      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth'
        });
      }
    };

    const anchors = document.querySelectorAll('a[href^="#"]');
    anchors.forEach(anchor => {
      anchor.addEventListener('click', handleSmoothScroll);
    });

    // Cleanup
    return () => {
      anchors.forEach(anchor => {
        anchor.removeEventListener('click', handleSmoothScroll);
      });
    };
  }, []);

  // Add scroll to top button functionality
  useEffect(() => {
    const scrollButton = document.getElementById('scrollToTop');
    
    const toggleScrollButton = () => {
      if (scrollButton) {
        if (window.scrollY > 300) {
          scrollButton.classList.add('opacity-100');
          scrollButton.classList.remove('opacity-0', 'pointer-events-none');
        } else {
          scrollButton.classList.add('opacity-0', 'pointer-events-none');
          scrollButton.classList.remove('opacity-100');
        }
      }
    };

    window.addEventListener('scroll', toggleScrollButton);
    return () => window.removeEventListener('scroll', toggleScrollButton);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Measure header height and update state
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };
    
    // Initial measurement
    updateHeaderHeight();
    
    // Update on window resize
    window.addEventListener('resize', updateHeaderHeight);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [location]);

  return (
    <div className="bg-white text-[#333A3D] min-h-screen flex flex-col">
      <Header isTransparent={isHomePage} />
      
      <main 
        className="flex-grow"
        style={{
          paddingTop: isHomePage ? 0 : `${headerHeight}px`,
        }}
      >
        {children}
      </main>
      
      <Footer />
      
      {/* Scroll to top button */}
      <button
        id="scrollToTop"
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 bg-white h-10 w-10 rounded-full shadow-lg flex items-center justify-center text-[#CC0033] opacity-0 pointer-events-none transition-all duration-300 z-30 hover:bg-[#FFEDD2]/30"
        aria-label="Scroll to top"
      >
        <ArrowUp className="h-5 w-5" />
      </button>
      
      {/* Background geometric pattern with subtle gradient overlay */}
      <div className="fixed inset-0 pointer-events-none z-[-1] opacity-5 pattern-bg"></div>
    </div>
  );
};

export default MainLayout;
